"use client";

import React, { useEffect, useRef } from "react";
import { motion, animate } from "motion/react";
import { useLang } from "@/context/lang";

function Counter({ value, suffix = "" }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  // Render the final value as the initial text — both for SSR (so crawlers
  // and no-JS users see the real number, not "0+") and as a fallback when
  // the section never enters the viewport.
  const finalText = value.toLocaleString() + suffix;
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    let triggered = false;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !triggered) {
        triggered = true;
        animate(0, value, {
          duration: 2.5, ease: "easeOut",
          onUpdate: (v) => { if (node) node.textContent = Math.round(v).toLocaleString() + suffix; },
        });
        obs.disconnect();
      }
    }, { threshold: 0.3 });
    obs.observe(node);
    return () => obs.disconnect();
  }, [value, suffix]);
  return <span ref={ref} suppressHydrationWarning>{finalText}</span>;
}

export function Stats() {
  const { T } = useLang();

  type Stat = { value: number; suffix: string; gradient: string; label: string; display?: React.ReactNode };
  const stats: Stat[] = [
    { value: 2600, suffix: "+", gradient: "from-cyan-500 to-teal-500", label: T("stats.voices") },
    { value: 25, suffix: "+", gradient: "from-teal-500 to-cyan-500", label: T("stats.languages") },
    { value: -1, suffix: "", gradient: "from-cyan-500 to-teal-500", label: T("stats.characters"),
      display: <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent">∞</span> },
    { value: -1, suffix: "", gradient: "from-cyan-500 to-teal-500", label: T("stats.uptime"),
      display: <span className="bg-gradient-to-r from-cyan-500 to-teal-500 bg-clip-text text-transparent text-3xl sm:text-4xl">TLS</span> },
  ];

  return (
    <section className="py-14 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900 border-y border-neutral-100 dark:border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-stretch justify-center gap-x-8 gap-y-6 sm:gap-x-12 lg:gap-x-16">
          {stats.map((stat, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="hidden sm:block w-px self-stretch bg-neutral-200 dark:bg-white/10" />}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ scale: 1.04 }}
                className="flex items-center gap-3 sm:gap-4"
              >
                <div className={`text-4xl sm:text-5xl font-black bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent leading-none whitespace-nowrap`}>
                  {stat.display || <Counter value={stat.value} suffix={stat.suffix} />}
                </div>
                <div className="text-xs sm:text-sm text-neutral-600 dark:text-slate-300 font-semibold tracking-wide leading-tight max-w-[120px] sm:max-w-none">
                  {stat.label}
                </div>
              </motion.div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
